import ReadyForAction from "@mui/icons-material/AccessTime";
import NotReadyForAction from "@mui/icons-material/DoNotDisturbOnTotalSilence";
import Done from "@mui/icons-material/Done";
import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import {
  ReviewMode,
  SubmissionVirtualEventsQuery,
  UserType,
} from "generated-graphql/graphql";

type Submission = NonNullable<
  SubmissionVirtualEventsQuery["submissionVirtualEvents"]["items"][0]["submission"]
>;
type Reviewer = Submission["reviewers"][number];

interface SubmissionReviewerCellProps {
  reviewers: Reviewer[];
  reviewMode?: ReviewMode | null;
  isStaff: boolean;
  type: "reviewer" | "signer";
}

/**
 * Determines if a reviewer has completed their review.
 * A reviewer is considered complete if any of their submissionDocumentReviews has a completedAt date.
 *
 * NOTE for humans and AI: The `submissionVirtualEvents` that provides the data for this component
 * only sends the most recent `SubmissionDocmentReview` record for each reviewer. So if the submission
 * has had mulitple rounds the review the table will correctly show the most recent SDR record results.
 */
export function hasCompletedReview(reviewer: Reviewer): boolean {
  return reviewer.submissionDocumentReviews.some(
    (review) => review.completedAt !== null
  );
}

/**
 * Checks if all non-signer reviewers have completed their reviews.
 */
export function allNonSignersCompleted(reviewers: Reviewer[]): boolean {
  const nonSigners = reviewers.filter((r) => !r.isSigner);
  return nonSigners.every(hasCompletedReview);
}

/**
 * Gets the display name for a reviewer.
 */
function getReviewerName(reviewer: Reviewer): string {
  const person = reviewer.user.person;
  if (person?.first || person?.last) {
    return `${person.first || ""} ${person.last || ""}`.trim();
  }
  return reviewer.user.email;
}

/**
 * Determines the status icon for a reviewer based on review mode.
 *
 * - Parallel mode: All reviewers can act simultaneously (AccessTimeFilled if not completed)
 * - Sequential mode: Reviewers must go in order by reviewOrder
 *   - AccessTimeFilled if it's their turn (all preceding reviewers completed)
 *   - Group if waiting for preceding reviewers
 */
function getReviewerIcon(
  reviewer: Reviewer,
  allReviewers: Reviewer[],
  reviewMode?: ReviewMode | null
): React.ReactNode {
  const iconSx = { height: "16px", width: "16px", mr: 0.5 };

  // 1. Completed - always show Done icon
  if (hasCompletedReview(reviewer)) {
    return <Done sx={{ ...iconSx, color: "action.active" }} />;
  }

  // 2. Signers must wait for all non-signers to complete (regardless of mode)
  if (reviewer.isSigner && !allNonSignersCompleted(allReviewers)) {
    return <NotReadyForAction sx={{ ...iconSx, color: "action.active" }} />;
  }

  // 3. Parallel mode (or no mode specified) - all non-completed can act
  if (reviewMode !== ReviewMode.Sequential) {
    return <ReadyForAction sx={{ ...iconSx, color: "action.active" }} />;
  }

  // 4. Sequential mode - check if preceding reviewers have finished
  const reviewerOrder = reviewer.reviewOrder ?? 0;
  const precedingReviewers = allReviewers.filter(
    (r) => (r.reviewOrder ?? 0) < reviewerOrder
  );
  const allPrecedingComplete = precedingReviewers.every(hasCompletedReview);

  if (allPrecedingComplete) {
    return <ReadyForAction sx={{ ...iconSx, color: "action.active" }} />;
  }

  return <NotReadyForAction sx={{ ...iconSx, color: "action.active" }} />;
}

/**
 * Gets the status label for a reviewer for the tooltip.
 */
export function getReviewerStatusLabel(
  reviewer: Reviewer,
  allReviewers: Reviewer[],
  reviewMode?: ReviewMode | null
): string {
  const DONE = "Done";
  const NOT_READY_FOR_ACTION = "Not ready for action";
  const READY_FOR_ACTION = "Ready for action";

  if (hasCompletedReview(reviewer)) {
    return DONE;
  }

  // Signers must wait for all non-signers to complete (regardless of mode)
  if (reviewer.isSigner && !allNonSignersCompleted(allReviewers)) {
    return NOT_READY_FOR_ACTION;
  }

  if (reviewMode !== ReviewMode.Sequential) {
    return READY_FOR_ACTION;
  }

  // Sequential mode
  const reviewerOrder = reviewer.reviewOrder ?? 0;
  const precedingReviewers = allReviewers.filter(
    (r) => (r.reviewOrder ?? 0) < reviewerOrder
  );
  const allPrecedingComplete = precedingReviewers.every(hasCompletedReview);

  if (allPrecedingComplete) {
    return READY_FOR_ACTION;
  }

  return NOT_READY_FOR_ACTION;
}

/**
 * A cell component for displaying reviewers with their status icons.
 * Replaces both the "Reviewer(s)" and "Waiting On" columns.
 */
export function SubmissionReviewerCell({
  reviewers,
  reviewMode,
  isStaff,
  type,
}: SubmissionReviewerCellProps) {
  const theme = useTheme();

  const filteredByType = reviewers.filter((r) =>
    type === "reviewer" ? !r.isSigner : r.isSigner
  );

  const displayedReviewers = filteredByType
    .filter((r) => isStaff || r.user.type === UserType.Customer)
    .sort((a, b) => (a.reviewOrder ?? 0) - (b.reviewOrder ?? 0));

  if (displayedReviewers.length === 0) {
    return null;
  }

  return (
    <Stack
      spacing={0}
      sx={{
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        height: "100%",
        width: "100%",
        alignItems: "start",
        overflow: "hidden",
      }}
    >
      {displayedReviewers.map((reviewer) => {
        const name = getReviewerName(reviewer);
        const icon = getReviewerIcon(reviewer, reviewers, reviewMode);
        const status = getReviewerStatusLabel(reviewer, reviewers, reviewMode);

        return (
          <Tooltip key={reviewer.id} title={status}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                overflow: "hidden",
              }}
            >
              {icon}
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {name}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
}
